// ONB-001 (Macrofase A) — qué dice la tarjeta de activación del Inicio. Lógica pura sobre lo que devolvió
// el servidor: aquí no se decide si hubo venta ni si hay artículo, solo cómo se cuenta.
//
//   Antes de la primera venta: el siguiente paso real (qué vende → primer artículo → primera venta).
//   Después: mejora progresiva, pocas recomendaciones y según lo que vende; se puede ocultar.
//   Sin nada pendiente, oculta, o en un negocio que no nació con el registro guiado: no hay tarjeta.
// Sin porcentajes ni «100 %»: nada obliga a completar todo.
import type { IconName } from '../components/ui/Icon';
import type { Activation, ActivationCommercial, ChecklistKey, RecommendationKey } from '../types/onboarding';
import { offeringNoun } from './quickProduct';

export interface RecommendationView {
  key: RecommendationKey;
  title: string;
  text: string;
  destination: string;
  actionLabel: string;
  icon: IconName;
}

export type ActivationCardView =
  | { kind: 'none' }
  | { kind: 'offering' }
  | { kind: 'first-item'; noun: string }
  | { kind: 'first-sale' }
  | { kind: 'progress'; items: RecommendationView[] };

/** Cuántas recomendaciones se muestran a la vez: pocas, nunca una lista infinita. */
export const MAX_RECOMMENDATIONS = 3;

const RECOMMENDATIONS: Record<RecommendationKey, Omit<RecommendationView, 'key'>> = {
  INVENTORY: { title: 'Registra tu inventario', text: 'Da de alta tus insumos y lo que tienes para saber qué se está acabando.', destination: 'inventory', actionLabel: 'Ir a Inventario', icon: 'inventory' },
  RECIPES: { title: 'Conecta tus productos con sus insumos', text: 'Con una receta, cada venta descuenta lo que usa.', destination: 'recipes', actionLabel: 'Ir a Recetas', icon: 'recipes' },
  EXPENSES: { title: 'Registra tus gastos', text: 'Lleva en un solo lugar lo que pagas: renta, insumos, servicios.', destination: 'expenses', actionLabel: 'Ir a Gastos', icon: 'expenses' },
  TEAM: { title: 'Suma a tu equipo', text: 'Crea un usuario para cada persona que te ayuda a vender.', destination: 'users', actionLabel: 'Ir a Usuarios', icon: 'users' },
};

export function activationCardView(activation: Activation | null | undefined): ActivationCardView {
  if (!activation || !activation.guided) return { kind: 'none' };
  if (activation.hasCompletedSale) {
    if (activation.dismissed) return { kind: 'none' };
    const items = activation.recommendations
      .filter((item) => !item.done)
      .slice(0, MAX_RECOMMENDATIONS)
      .map((item) => ({ key: item.key, ...RECOMMENDATIONS[item.key] }));
    return items.length > 0 ? { kind: 'progress', items } : { kind: 'none' };
  }
  if (activation.offering === null) return { kind: 'offering' };
  if (!activation.hasSellableItem) return { kind: 'first-item', noun: offeringNoun(activation.offering) };
  return { kind: 'first-sale' };
}

// ── P-085 · el checklist en tres niveles y la condición comercial ────────────────────────────────
//
// La tarjeta de arriba (`activationCardView`) no cambia: dice el SIGUIENTE paso. El checklist completa lo que se ve:
// antes de la primera venta, lo indispensable con su marca; después, lo recomendado y lo opcional (con «Reportes» como
// acceso, sin marca). Lo decide el servidor (campo `checklist`); con un servidor anterior no hay checklist y la tarjeta
// queda como siempre.

export interface ChecklistItemView {
  key: ChecklistKey;
  title: string;
  text?: string;
  /** null = un acceso que no se marca. */
  done: boolean | null;
  destination?: string;
  actionLabel?: string;
  icon: IconName;
}

export interface ActivationChecklistView {
  essentials: ChecklistItemView[];
  recommended: ChecklistItemView[];
  optional: ChecklistItemView[];
  /** Cuántos de recomendado + opcional faltan (sin contar los accesos sin marca). */
  pendingAfterAha: number;
}

const POST_AHA: Partial<Record<ChecklistKey, Omit<ChecklistItemView, 'key' | 'done'>>> = {
  INVENTORY: RECOMMENDATIONS.INVENTORY,
  RECIPES: RECOMMENDATIONS.RECIPES,
  EXPENSES: RECOMMENDATIONS.EXPENSES,
  TEAM: RECOMMENDATIONS.TEAM,
  COSTS: { title: 'Anota cuánto te cuesta', text: 'Con un costo aproximado o una receta, sabes cuánto ganas en cada venta.', destination: 'other-sales/catalog', actionLabel: 'Ir al catálogo', icon: 'coins' },
  REPORTS: { title: 'Mira tus reportes', text: 'Cuánto vendes, cuándo vendes más y qué productos te convienen.', destination: 'owner-dashboard', actionLabel: 'Ver reportes', icon: 'reports' },
};

function essentialTitle(key: ChecklistKey, noun: string): string {
  if (key === 'ACCOUNT') return 'Tu cuenta y tu correo';
  if (key === 'BUSINESS') return 'Tu negocio y tu primera tienda';
  if (key === 'OFFERING') return 'Qué vas a vender';
  if (key === 'FIRST_ITEM') return `Tu primer ${noun}`;
  return 'Tu primera venta';
}

const ESSENTIAL_ICON: Partial<Record<ChecklistKey, IconName>> = {
  ACCOUNT: 'users', BUSINESS: 'stores', OFFERING: 'bulb', FIRST_ITEM: 'plus', FIRST_SALE: 'sell',
};

/** El checklist que se dibuja, o null (servidor anterior o negocio no guiado). Puro. */
export function activationChecklistView(activation: Activation | null | undefined): ActivationChecklistView | null {
  const checklist = activation?.guided ? activation.checklist : undefined;
  if (!checklist) return null;
  const noun = offeringNoun(activation!.offering);
  const essentials = checklist.essential.map((item) => ({
    key: item.key, title: essentialTitle(item.key, noun), done: item.done, icon: ESSENTIAL_ICON[item.key] ?? 'check',
  }));
  const later = (items: typeof checklist.recommended) => items
    .filter((item) => POST_AHA[item.key])
    .map((item) => ({ key: item.key, done: item.done, ...POST_AHA[item.key]! }));
  const recommended = later(checklist.recommended);
  const optional = later(checklist.optional);
  const pendingAfterAha = [...recommended, ...optional].filter((item) => item.done === false).length;
  return { essentials, recommended, optional, pendingAfterAha };
}

/** El indicador discreto del encabezado del Inicio, o null. Puro. */
export function commercialNote(commercial: ActivationCommercial | null | undefined): string | null {
  if (!commercial) return null;
  if (commercial.kind === 'PRIVATE_BETA') return 'Beta privada · sin costo';
  const days = commercial.daysLeft === 1 ? 'queda 1 día' : `quedan ${commercial.daysLeft} días`;
  return `Prueba gratis · ${days}`;
}
